import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    Alert,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Card,
    CardContent,
    Grid,
    Chip,
    Divider,
    styled,
    Skeleton,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns/format';
import { tr } from 'date-fns/locale';
import { getFinalSinav, getFinalSinavSonucPdf } from '../../../services/api/sinavService';
import { SinavError } from '../../../types/ErrorModals/SinavError';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'; 
import SchoolIcon from '@mui/icons-material/School';
import { SinavDetayRequestModal } from '../../../types/RequestModals/SinavDetayRequestModal';
import { getCurrentSezon, getSezonOptions } from '../../../utils/sezon';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: theme.shadows[8],
    },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'scale(1.15)',
    },
}));

const SeasonSelector = styled(FormControl)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius * 1.5,
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '0.75rem',
}));

const FinalSinav: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const { kullaniciModal } = useAuth();
    const [selectedSeason, setSelectedSeason] = useState<string>(String(getCurrentSezon()));
    const availableSeasons = getSezonOptions().map(String);
    
    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
    };
    
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['finalSinav', kullaniciModal?.id, selectedSeason],
        queryFn: async () => {
            try {
                return await getFinalSinav({
                    kurumKodu: localStorage.getItem('kurumKodu') || '',
                    ogrenciId: kullaniciModal?.id || '',
                    sezon: parseInt(selectedSeason)
                });
            } catch (err) {
                const sinavError = err as SinavError;
                throw new Error(sinavError.message);
            }
        },
        enabled: !!kullaniciModal?.id,
        retry: 1
    });
    
    useEffect(() => {
        if (kullaniciModal?.id) {
            refetch();
        }
    }, [selectedSeason, refetch, kullaniciModal?.id]);
    
    const handleSinavGoruntule = async (sinavId: number) => {
        try {
            const requestData: SinavDetayRequestModal = {
                kurumKodu: localStorage.getItem('kurumKodu') || '',
                sinavId: sinavId,
                ogrenciId: Number(kullaniciModal?.id) || 0,
                sezon: parseInt(selectedSeason)
            };
            
            const response = await getFinalSinavSonucPdf(requestData);
            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `final_sinav_sonuc_${sinavId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Sınav görüntüleme hatası:', error);
        }
    };
    
    // Render card view for mobile and tablet
    const renderCardView = () => {
        return (
            <Grid container spacing={2}>
                {data?.map((sinav, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <StyledCard elevation={3}>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ mb: 2 }}>
                                    <StyledChip 
                                        label="Final"
                                        size="small"
                                        color="secondary"
                                        icon={<SchoolIcon fontSize="small" />}
                                    />
                                </Box>
                                
                                <Typography variant="h6" component="h2" gutterBottom noWrap>
                                    {sinav.sinavAdi}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <CalendarTodayIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {format(new Date(sinav.tarih), 'dd MMMM yyyy', { locale: tr })}
                                    </Typography>
                                </Box>
                                
                                <Divider sx={{ my: 1.5 }} />
                                
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Tooltip title="PDF olarak İndir">
                                        <ActionButton
                                            onClick={() => handleSinavGoruntule(sinav.sinavId)}
                                            color="secondary"
                                            size="medium"
                                        >
                                            <PictureAsPdfIcon />
                                        </ActionButton>
                                    </Tooltip>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Grid>
                ))}
            </Grid>
        );
    };
    
    // Render table view for desktop
    const renderTableView = () => {
        return (
            <TableContainer 
                component={Paper} 
                elevation={3} 
                sx={{ 
                    minWidth: 800,
                    borderRadius: theme.shape.borderRadius * 1.5,
                    overflow: 'hidden'
                }}
            >
                <Table aria-label="final sınavlar tablosu">
                    <TableHead>
                        <TableRow>
                            <TableCell 
                                sx={{ 
                                    fontWeight: "bold", 
                                    backgroundColor: theme.palette.secondary.main, 
                                    color: "white",
                                    py: 2
                                }}
                            >
                                Tarih
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    fontWeight: "bold", 
                                    backgroundColor: theme.palette.secondary.main, 
                                    color: "white",
                                    py: 2
                                }}
                            >
                                Sınav Adı
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    fontWeight: "bold", 
                                    backgroundColor: theme.palette.secondary.main, 
                                    color: "white", 
                                    textAlign: "center",
                                    py: 2
                                }}
                            >
                                İşlemler
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.map((sinav, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
                                    "&:hover": {
                                        backgroundColor: "rgba(156, 39, 176, 0.1)", // Purple for final exams
                                        transition: "background-color 0.3s ease",
                                    },
                                }}
                            >
                                <TableCell>
                                    {format(new Date(sinav.tarih), 'dd MMMM yyyy', { locale: tr })}
                                </TableCell>
                                <TableCell>{sinav.sinavAdi}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        <Tooltip title="PDF olarak İndir" arrow>
                                            <ActionButton
                                                onClick={() => handleSinavGoruntule(sinav.sinavId)}
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                }}
                                                size="small"
                                            >
                                                <PictureAsPdfIcon />
                                            </ActionButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };
    
    // Loading skeletons for better UX
    const renderSkeletons = () => {
        if (isTablet) {
            return (
                <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Skeleton variant="rectangular" width={80} height={24} sx={{ mb: 2 }} />
                                    <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
                                    <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />
                                    <Skeleton variant="rectangular" height={1} sx={{ my: 1.5 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            );
        }
        
        return (
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {[1, 2, 3].map((item) => (
                                <TableCell key={item} sx={{ backgroundColor: theme.palette.secondary.main }}>
                                    <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[1, 2, 3, 4].map((row) => (
                            <TableRow key={row}>
                                {[1, 2, 3].map((cell) => (
                                    <TableCell key={cell}>
                                        <Skeleton variant="text" />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };
    
    return (
        <Box sx={{ px: { xs: 1, sm: 2 } }}>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 3
            }}>
                <Typography 
                    variant="h5" 
                    component="h1"
                    sx={{ 
                        fontWeight: 600, 
                        mb: { xs: 2, sm: 0 },
                        color: theme.palette.text.primary
                    }}
                >
                    <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Sınav Sonuçları
                </Typography>
                
                <SeasonSelector
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                >
                    <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                    <Select
                        labelId="season-select-label"
                        id="season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        label="Eğitim Sezonu"
                    >
                        {availableSeasons.map((season) => (
                            <MenuItem key={season} value={season}>
                                {parseInt(season) - 1} - {season} Eğitim Yılı
                            </MenuItem>
                        ))}
                    </Select>
                </SeasonSelector>
            </Box>
            
            {isLoading ? (
                renderSkeletons()
            ) : error ? (
                <Alert
                    severity="error"
                    variant="filled"
                    sx={{ 
                        mt: 2, 
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[3]
                    }}
                    action={
                        process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block" sx={{ color: 'white' }}>
                                CorrelationId: {(error as SinavError).correlationId}
                            </Typography>
                        )
                    }
                >
                    {(error as Error).message}
                </Alert>
            ) : !data?.length ? (
                <Alert 
                    severity="info" 
                    variant="outlined"
                    icon={<CalendarTodayIcon />}
                    sx={{ 
                        mt: 2, 
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[1],
                        bgcolor: theme.palette.background.paper
                    }}
                >
                    <Typography variant="body1">
                        {parseInt(selectedSeason) - 1} - {selectedSeason} eğitim sezonunda kayıtlı sınav bulunmamaktadır.
                    </Typography>
                </Alert>
            ) : (
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    {isTablet ? renderCardView() : renderTableView()}
                </Box>
            )}
        </Box>
    );
};

export default FinalSinav;